import type { CatalogFilterPageKind } from '../types';

// Matches legacy GET listing routes: /fanfics or /originals (no extra path segments)
export const LEGACY_LISTING_ROUTE_PATTERN = /^\/(fanfics|originals)\/?$/;

// Matches permalink fandom page: /fanfics/fandoms/{fandomSlug}
export const FANDOM_PERMALINK_PATTERN = /^\/fanfics\/fandoms\/([^/]+)\/?$/;

// Matches fandom characters hub: /fanfics/fandoms/{fandomSlug}/characters
export const FANDOM_CHARACTERS_HUB_PATTERN = /^\/fanfics\/fandoms\/([^/]+)\/characters\/?$/;

// Matches permalink character page: /fanfics/fandoms/{fandomSlug}/characters/{characterSlug}
export const CHARACTER_PERMALINK_PATTERN = /^\/fanfics\/fandoms\/([^/]+)\/characters\/([^/]+)\/?$/;

// Matches permalink pairing page: /fanfics/fandoms/{fandomSlug}/pairings/{pairingSlug}
export const PAIRING_PERMALINK_PATTERN = /^\/fanfics\/fandoms\/([^/]+)\/pairings\/([^/]+)\/?$/;

// Previous permalink shapes — middleware candidates so backend can 301 onto /fanfics/fandoms/...
export const LEGACY_FANDOM_SLUG_PATTERN =
  /^\/fanfics\/(?!fandoms$|characters$|tags$|\d+$)([^/]+)\/?$/;

export const LEGACY_CHARACTER_PERMALINK_PATTERN =
  /^\/fanfics\/(?!fandoms$)([^/]+)\/characters\/([^/]+)\/?$/;

export const LEGACY_PAIRING_PERMALINK_PATTERN =
  /^\/fanfics\/(?!fandoms$)([^/]+)\/pairings\/([^/]+)\/?$/;

export const LEGACY_PERMALINK_PATTERNS = [
  LEGACY_FANDOM_SLUG_PATTERN,
  LEGACY_CHARACTER_PERMALINK_PATTERN,
  LEGACY_PAIRING_PERMALINK_PATTERN,
];

// Matches permalink tag page for fanfics: /fanfics/tags/{tagSlug}
export const FANFIC_TAG_PERMALINK_PATTERN = /^\/fanfics\/tags\/([^/]+)\/?$/;

// Matches permalink tag page for originals: /originals/tags/{tagSlug}
export const ORIGINAL_TAG_PERMALINK_PATTERN = /^\/originals\/tags\/([^/]+)\/?$/;

// Matches hub routes: /fanfics/fandoms, /fanfics/characters
export const HUB_ROUTE_PATTERN = /^\/fanfics\/(fandoms|characters)\/?$/;

// All live permalink patterns, checked in order (most specific first).
export const ALL_PERMALINK_PATTERNS = [
  CHARACTER_PERMALINK_PATTERN,
  PAIRING_PERMALINK_PATTERN,
  FANDOM_PERMALINK_PATTERN,
  FANFIC_TAG_PERMALINK_PATTERN,
  ORIGINAL_TAG_PERMALINK_PATTERN,
  HUB_ROUTE_PATTERN,
];

/**
 * Determines whether a given pathname is a SEO permalink route (fandom,
 * character, pairing, tag, or hub page) as opposed to a legacy GET-filter
 * listing route (/fanfics or /originals with query-string filters).
 *
 * Used on the client to switch the URL-sync mode: on permalink routes,
 * the address bar is exclusively managed by useSeoRedirect's pushState/
 * replaceState calls, and the legacy filters->query-string sync effect
 * must be fully disabled to avoid racing with it.
 */
export const isPermalinkPath = (pathname: string): boolean =>
  ALL_PERMALINK_PATTERNS.some((pattern) => pattern.test(pathname));

/**
 * Maps a catalog pathname to the locked-facet kind used to load related
 * filter option lists. Hub routes and unknown paths fall back to catalog.
 */
export const getCatalogFilterPageKind = (pathname: string): CatalogFilterPageKind => {
  if (CHARACTER_PERMALINK_PATTERN.test(pathname)) return 'character';
  if (PAIRING_PERMALINK_PATTERN.test(pathname)) return 'pairing';
  if (FANDOM_PERMALINK_PATTERN.test(pathname)) return 'fandom';
  if (
    FANFIC_TAG_PERMALINK_PATTERN.test(pathname) ||
    ORIGINAL_TAG_PERMALINK_PATTERN.test(pathname)
  ) {
    return 'tag';
  }
  return 'catalog';
};

export interface PermalinkListingSlugs {
  fandomSlug?: string;
  characterSlug?: string;
  pairingSlug?: string;
  tagSlug?: string;
}

/**
 * Extracts permalink slug segments so fanfics-custom/v2 can be queried with
 * fandomSlug / characterSlug / pairingSlug / tagSlug instead of numeric ids.
 */
export const getPermalinkListingSlugs = (pathname: string): PermalinkListingSlugs | null => {
  const characterMatch = CHARACTER_PERMALINK_PATTERN.exec(pathname);
  if (characterMatch) {
    return { fandomSlug: characterMatch[1], characterSlug: characterMatch[2] };
  }

  const pairingMatch = PAIRING_PERMALINK_PATTERN.exec(pathname);
  if (pairingMatch) {
    return { fandomSlug: pairingMatch[1], pairingSlug: pairingMatch[2] };
  }

  const fandomMatch = FANDOM_PERMALINK_PATTERN.exec(pathname);
  if (fandomMatch) {
    return { fandomSlug: fandomMatch[1] };
  }

  const fanficTagMatch = FANFIC_TAG_PERMALINK_PATTERN.exec(pathname);
  if (fanficTagMatch) {
    return { tagSlug: fanficTagMatch[1] };
  }

  const originalTagMatch = ORIGINAL_TAG_PERMALINK_PATTERN.exec(pathname);
  if (originalTagMatch) {
    return { tagSlug: originalTagMatch[1] };
  }

  return null;
};

/**
 * Paths that should be sent to /api/v1/seo/redirect in proxy.ts: live
 * permalinks plus previous /fanfics/{slug} shapes, so old URLs can still
 * be 301'd to /fanfics/fandoms/{slug}/...
 */
export const isSeoRedirectCandidatePath = (pathname: string): boolean =>
  isPermalinkPath(pathname) || LEGACY_PERMALINK_PATTERNS.some((pattern) => pattern.test(pathname));

// Filter-state dimensions (matching FilterState keys, minus the "selected"
// prefix) that a given permalink pathname already encodes in its path
// segments. Used to strip those query params when writing the address bar
// on a permalink route — otherwise a "non-canonicalizing" filter change
// (e.g. ratings/statuses, which don't trigger a new /api/v1/seo/redirect
// call) would append a redundant/stale copy of the path-encoded dimension
// back into the query string (e.g. /fanfics/fandoms/naruto?fandoms=10529&ratings=0
// instead of the correct /fanfics/fandoms/naruto?ratings=0).
export const getPermalinkEncodedFilterKeys = (
  pathname: string,
): Array<'fandoms' | 'tags' | 'characters' | 'pairingCharacters'> => {
  if (CHARACTER_PERMALINK_PATTERN.test(pathname)) {
    return ['fandoms', 'characters'];
  }
  if (PAIRING_PERMALINK_PATTERN.test(pathname)) {
    return ['fandoms', 'pairingCharacters'];
  }
  if (
    FANFIC_TAG_PERMALINK_PATTERN.test(pathname) ||
    ORIGINAL_TAG_PERMALINK_PATTERN.test(pathname)
  ) {
    return ['tags'];
  }
  if (FANDOM_PERMALINK_PATTERN.test(pathname)) {
    return ['fandoms'];
  }
  return [];
};

export const getFandomPermalinkBasePath = (pathname: string): string | null => {
  const character = CHARACTER_PERMALINK_PATTERN.exec(pathname);
  if (character?.[1]) return `/fanfics/fandoms/${character[1]}`;
  const pairing = PAIRING_PERMALINK_PATTERN.exec(pathname);
  if (pairing?.[1]) return `/fanfics/fandoms/${pairing[1]}`;
  const hub = FANDOM_CHARACTERS_HUB_PATTERN.exec(pathname);
  if (hub?.[1]) return `/fanfics/fandoms/${hub[1]}`;
  const fandom = FANDOM_PERMALINK_PATTERN.exec(pathname);
  if (fandom?.[1]) return `/fanfics/fandoms/${fandom[1]}`;
  return null;
};

// Query param keys considered SEO-relevant for legacy GET filter detection
export const SEO_LEGACY_FILTER_PARAM_KEYS = [
  'fandom',
  'fandoms',
  'tags',
  'characters',
  'pairing',
  'pairingCharacters',
];
